import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '@Services/apiAxios';

interface PurchaseInvoiceDownloadResponse {
  msg: string;
  data: {
    url: string;
  };
}

// 🔹 Type for filter params
export interface PurchaseInvoiceDownloadParams {
  fromDate: string;
  toDate: string;
  downloadType: string;
  systems?: string[];
  invoice_type?: string[];
  type?: string[];
  status?: string[];
  searchKey?: string;
  searchValue?: string;
  carriers?: string[];
  currentPage: number;
  perPage: number;
}

interface PurchaseInvoiceDownloadState {
  url: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: PurchaseInvoiceDownloadState = {
  url: null,
  loading: false,
  error: null,
};

// ✅ Async thunk with full filter params
export const fetchPurchaseInvoiceDownload = createAsyncThunk<
  string, // return type (url)
  PurchaseInvoiceDownloadParams, // params
  { rejectValue: string }
>('purchaseInvoiceDownload/fetch', async (params, { rejectWithValue }) => {
  try {
    const response = await API.get<PurchaseInvoiceDownloadResponse>(`/invoice/purchase-invoice/download-list`, {
      params,
    });

    return response.data.data.url;
  } catch (error: any) {
    const message = error.response?.data?.msg || error.message || 'Something went wrong';
    return rejectWithValue(message);
  }
});

const purchaseInvoiceDownloadSlice = createSlice({
  name: 'purchaseInvoiceDownload',
  initialState,
  reducers: {
    resetPurchaseInvoiceDownloadState: state => {
      state.url = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPurchaseInvoiceDownload.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseInvoiceDownload.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.url = action.payload;
      })
      .addCase(fetchPurchaseInvoiceDownload.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch data';
      });
  },
});

export const { resetPurchaseInvoiceDownloadState } = purchaseInvoiceDownloadSlice.actions;
export default purchaseInvoiceDownloadSlice.reducer;
